/**
 * @module store/mutations
 * @category Gerenciador de estados
 * @summary Módulo de _mutations_ do estado do sistema.
 *
 * @requires module:constants.MODULOS_ROUTE
 * @requires module:constants.STORAGE_KEY_PREFIX_INFOCARD
 * @requires module:utils/web/Storage/Local~Local
 */
import { MODULOS_ROUTE, STORAGE_KEY_PREFIX_INFOCARD } from "../constants";
import { Local } from "../utils/web/Storage";

/**
 * Mapeia algumas propriedades de um registro de rota (utiliza as propriedades
 * `path` e `meta`.`title`) em um objeto aceito pelo componente
 * {@link module:components/base/BaseBreadcrumbs/BaseBreadcrumbs}.
 * @function
 * @param {external:RouteRecord} routeMatched - Registro de rota onde houve
 * match de acordo com o caminho.
 * @returns {object} Objeto com as propriedades `to` e `content`.
 * @ignore
 */
const mapRouteMatchedToBreadcrumb = function(routeMatched) {
  const content = routeMatched.meta ? routeMatched.meta.title : "";
  const to = routeMatched.path;

  return {
    content,
    to
  };
};

/**
 * Adiciona no estado o código de um módulo baixado e parseado pelo sistema.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} downloadedModulo - Código de um módulo baixado e parseado
 * pelo sistema.
 */
export const addDownloadedModulo = function(state, downloadedModulo) {
  if (!downloadedModulo) {
    return;
  }

  state.downloadedModulos.push(downloadedModulo);
};

/**
 * Adiciona no estado o `id` de um _infocard_.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {string} id - `id` do _infocard_.
 */
export const addInfoCardId = function(state, id) {
  if (!id) {
    return;
  }

  state.infoCardIds.push(id);
};

/**
 * Importa para o estado os metadados do _infocard_ armazenado no
 * {@link external:Storage Storage} do tipo "local".
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {string} id - `id` do _infocard_.
 */
export const importInfoCard = function(state, id) {
  const key = `${STORAGE_KEY_PREFIX_INFOCARD}/${id}`;
  const value = JSON.parse(Local.get(key));

  if (value) {
    state.infoCards[id] = value;
  }
};

/**
 * Restaura a exibição de um _infocard_.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} id - `id` do _infocard_.
 */
export const restoreInfoCard = function(state, id) {
  if (!id) {
    return;
  }

  const value = state.infoCards[id];

  if (value && value.isHidden) {
    value.isHidden = false;

    setInfoCard(state, { id, value });
  }
};

/**
 * Remove um _infocard_ do estado do sistema.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} id - `id` do _infocard_.
 */
export const removeInfoCard = function(state, id) {
  if (!id) {
    return;
  }

  delete state.infoCards[id];
};

/**
 * Adiciona (ou atualiza) no estado os metadados de um _infocard_. Utiliza o
 * {@link external:Storage Storage} do tipo "local" para realizar a persistência dos metadados.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} metadata - Metadados do _infocard_.
 */
export const setInfoCard = function(state, metadata) {
  if (!metadata || !metadata.id) {
    return;
  }

  const infoCards = { ...state.infoCards };

  const id = metadata.id;
  const value = { ...metadata.value };

  infoCards[id] = value;

  state.infoCards = infoCards;

  const key = `${STORAGE_KEY_PREFIX_INFOCARD}/${id}`;

  Local.set(key, value);
};

/**
 * Adiciona no estado o objeto com os dados de uma permissão/_role_.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} permission - Objeto com os dados de uma permissão/_role_.
 */
export const addPermission = function(state, permission) {
  if (!permission || !permission.nome) {
    return;
  }

  const permissions = { ...state.permissions };

  permissions[permission.nome] = { ...permission };

  state.permissions = permissions;
};

/**
 * Limpa as permissões do usuário logado.
 * @function
 */
export const clearPermissions = function(state) {
  state.permissions = {};
};

/**
 * Define qual módulo está ativo no momento.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} metadata - Metadados do módulo ativo.
 */
export const setModulo = function(state, metadata) {
  state.modulo = metadata;
};

/**
 * Atribui ao estado um _Array_ com os itens do menu do módulo ativo.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {Array<object>} menu - Itens do menu. Cada objeto deve possuir
 * as props aceitas pelo componente {@link module:components/base/BaseRouterLink/BaseRouterLink BaseRouterLink}
 * e uma propriedade `content`. Nos casos em que o menu precisa ser um elemento
 * HTML `a`, o objeto correspondente precisa ter, além da propriedade `content`,
 * a propriedade `isLink` com valor `true` e, opcionalmente, as propriedades
 * `href` e `onClick`.
 */
export const setModuloMenu = function(state, menu) {
  state.moduloMenu = menu || [];
};

/**
 * _Alias_ de {@link module:store/mutations.setModuloMenu setModuloMenu}.
 * @function
 * @deprecated
 * @param {object} state - Estado global do sistema.
 * @param {Array<object>} menu - Itens do menu. Cada objeto deve possuir
 * as props aceitas pelo componente {@link module:components/base/BaseRouterLink/BaseRouterLink BaseRouterLink}
 * e uma propriedade `content`. Nos casos em que o menu precisa ser um elemento
 * HTML `a`, o objeto correspondente precisa ter, além da propriedade `content`,
 * a propriedade `isLink` com valor `true` e, opcionalmente, as propriedades
 * `href` e `onClick`.
 */
export const setActiveModuleMenu = function(state, menu) {
  setModuloMenu(state, menu);
};

/**
 * Adiciona um item de menu ao _Array_ de itens de menu do módulo ativo.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} menuItem - Item de menu. Deve possuir as props aceitas pelo
 * componente {@link module:components/base/BaseRouterLink/BaseRouterLink BaseRouterLink}
 * e uma propriedade `content`. Nos casos em que o menu precisa ser um elemento
 * HTML `a`, o objeto correspondente precisa ter, além da propriedade `content`,
 * a propriedade `isLink` com valor `true` e, opcionalmente, as propriedades
 * `href` e `onClick`.
 */
export const addModuloMenuItem = function(state, menuItem) {
  if (!menuItem) {
    return;
  }

  state.moduloMenu.push(menuItem);
};

/**
 * Atribui ao estado os dados de autenticação do usuário logado.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} authenticationData - Dados de autenticação.
 */
export const setAuthenticationData = function(state, authenticationData) {
  state.authenticationData = authenticationData;
};

/**
 * Atribui ao estado o modo de autenticação utilizado.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} authenticationMode - Modo de autenticação.
 */
export const setAuthenticationMode = function(state, authenticationMode) {
  state.authenticationMode = authenticationMode;
};

/**
 * Atribui ao estado um indicador de se o sistema está carregando um módulo ou não.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {boolean} loadingModulo - Valor para indicar se o sistema está
 * carregando um módulo ou não.
 */
export const setLoadingModulo = function(state, loadingModulo) {
  state.loadingModulo = loadingModulo;
};

/**
 * Atribui ao estado um _Array_ com os metadados dos módulos registrados no sistema.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {Array<object>} modulos - Metadados dos módulos.
 */
export const setModulos = function(state, modulos) {
  state.modulos = modulos || [];
};

/**
 * Atribui ao estado o ticket retornado pelo {@link http://cas.ufrj.br CAS} da UFRJ.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {string} ticket - Ticket do {@link http://cas.ufrj.br CAS} da UFRJ.
 */
export const setTicket = function(state, ticket) {
  state.ticket = ticket;
};

/**
 * Atribui ao estado os dados do servidor responsável pelo usuário logado no
 * sistema.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} user - Dados do servidor responsável pelo usuário logado no
 * sistema.
 */
export const setUser = function(state, user) {
  state.user = user;
};

/**
 * Atualiza os _breadcrumbs_ armazenados no estado de um módulo do sistema.
 * @function
 * @param {object} state - Estado global do sistema.
 * @param {object} payload
 * @param {string} payload.moduloSystemName - Nome mnemônico do módulo.
 * @param {external:RouteRecord} payload.route - Registro de rota acessada pelo
 * browser.
 */
export const updateBreadcrumbs = function(state, payload) {
  if (!payload || !payload.moduloSystemName || !payload.route) {
    return;
  }

  // Módulo não possui suporte a breadcrumbs
  if (state[payload.moduloSystemName].breadcrumbs === undefined) {
    return;
  }

  const breadcrumbs = [];

  const matched = payload.route.matched;

  // A primeira rota é sempre o nome do módulo
  breadcrumbs.push(mapRouteMatchedToBreadcrumb(matched[0]));

  for (let i = 1; i < matched.length; i++) {
    if (
      matched[i].path !== `${MODULOS_ROUTE}/${payload.moduloSystemName}/` &&
      !matched[i].path.endsWith("home") &&
      !matched[i].path.endsWith("home/")
    ) {
      breadcrumbs.push(mapRouteMatchedToBreadcrumb(matched[i]));
    }
  }

  if (payload.route.query && breadcrumbs.length) {
    let breadcrumb = breadcrumbs[breadcrumbs.length - 1];

    breadcrumb.to = {
      path: breadcrumb.to,
      query: payload.route.query
    };
  }

  state[payload.moduloSystemName].breadcrumbs = breadcrumbs;
};
